import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ImageUpload from "@/components/ImageUpload";
import PublicBookingSettingsCard from "@/components/PublicBookingSettingsCard";
import PublicPaymentSettingsCard from "@/components/PublicPaymentSettingsCard";
import PublicWhatsAppHelpCard from "@/components/PublicWhatsAppHelpCard";
import RequiredPatientFieldsCard from "@/components/RequiredPatientFieldsCard";
import FollowUpMessageSettingsCard from "@/components/FollowUpMessageSettingsCard";
import SquareSettingsCard from "@/components/SquareSettingsCard";
import WhatsAppMetaCard from "@/components/WhatsAppMetaCard";
import LandingContentSettingsCard from "@/components/LandingContentSettingsCard";
import TestimonialsSettingsCard from "@/components/TestimonialsSettingsCard";
import GallerySettingsCard from "@/components/GallerySettingsCard";
import NewsPostsSettingsCard from "@/components/NewsPostsSettingsCard";
import ZoneCatalogSettingsCard from "@/components/ZoneCatalogSettingsCard";
import ClinicBusinessHoursCard from "@/components/ClinicBusinessHoursCard";
import AcceptedPaymentMethodsCard from "@/components/AcceptedPaymentMethodsCard";
import FinancingSettingsCard from "@/components/FinancingSettingsCard";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { CLINIC, LANDING_ADMIN } from "@/constants/testIds";

const TIMEZONES = ["America/Chicago", "America/New_York", "America/Denver", "America/Los_Angeles"];
const AVAILABLE_LANGUAGES = [{ code: "es", label: "Espanol" }, { code: "en", label: "Ingles" }];

export default function SettingsPage() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    api.get("/clinic").then(({ data }) => setForm(data));
  }, []);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const toggleLanguage = (code) => {
    setForm((f) => ({
      ...f,
      languages: f.languages.includes(code) ? f.languages.filter((l) => l !== code) : [...f.languages, code],
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/clinic", {
        name: form.name,
        address: form.address,
        phone: form.phone,
        email: form.email,
        whatsapp_number: form.whatsapp_number || null,
        timezone: form.timezone,
        languages: form.languages,
        brand_primary_color: form.brand_primary_color,
        brand_secondary_color: form.brand_secondary_color,
        logo_url: form.logo_url,
      });
      setForm(data);
      toast({ title: "Configuración guardada", description: "Los datos de tu clínica fueron actualizados." });
    } catch (err) {
      toast({ title: "Error al guardar", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  if (!form) return <p className="text-center text-muted-foreground py-12">Cargando configuración...</p>;

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="font-serif text-3xl sm:text-4xl tracking-tight text-foreground">Configuración</h1>
        <p className="text-base text-muted-foreground mt-1">Datos, marca e idiomas de tu clínica.</p>
      </div>

      <div className="bg-white border border-border rounded-xl p-6 sm:p-8 shadow-sm space-y-5">
        <div>
          <Label className="text-sm text-muted-foreground mb-1 block">Logo de la clínica</Label>
          <ImageUpload currentUrl={form.logo_url} onUploaded={(url) => setForm((f) => ({ ...f, logo_url: url }))} placeholderUrl={null} testId={CLINIC.logoUpload} shape="square" />
        </div>

        <div>
          <Label className="text-sm text-muted-foreground mb-1 block">Nombre de la clínica</Label>
          <Input value={form.name} onChange={set("name")} data-testid={CLINIC.nameInput} />
        </div>

        <div>
          <Label className="text-sm text-muted-foreground mb-1 block">Dirección</Label>
          <Input value={form.address || ""} onChange={set("address")} data-testid={CLINIC.addressInput} placeholder="Houston, TX, USA" />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Teléfono</Label>
            <Input value={form.phone || ""} onChange={set("phone")} data-testid={CLINIC.phoneInput} />
          </div>
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Email de contacto</Label>
            <Input type="email" value={form.email || ""} onChange={set("email")} data-testid={CLINIC.emailInput} />
          </div>
        </div>

        <div>
          <Label className="text-sm text-muted-foreground mb-1 block">Número de WhatsApp institucional</Label>
          <Input value={form.whatsapp_number || ""} onChange={set("whatsapp_number")} placeholder="+1 713 493 5555" data-testid={CLINIC.whatsappNumberInput} />
          <p className="text-xs text-muted-foreground mt-1">Este es el número que recibe los mensajes del Agente de WhatsApp (distinto del botón de ayuda de la reserva pública).</p>
        </div>

        <div>
          <Label className="text-sm text-muted-foreground mb-1 block">Zona horaria</Label>
          <Select value={form.timezone} onValueChange={(v) => setForm((f) => ({ ...f, timezone: v }))}>
            <SelectTrigger data-testid={CLINIC.timezoneSelect}><SelectValue /></SelectTrigger>
            <SelectContent>
              {TIMEZONES.map((tz) => <SelectItem key={tz} value={tz}>{tz}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="text-sm text-muted-foreground mb-1 block">Idiomas habilitados</Label>
          <div className="flex gap-4">
            {AVAILABLE_LANGUAGES.map((lang) => (
              <label key={lang.code} className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox checked={form.languages.includes(lang.code)} onCheckedChange={() => toggleLanguage(lang.code)} data-testid={`${CLINIC.languageCheckboxPrefix}${lang.code}`} />
                {lang.label}
              </label>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Color primario</Label>
            <div className="flex items-center gap-2">
              <Input type="color" value={form.brand_primary_color} onChange={set("brand_primary_color")} className="w-14 p-1" data-testid={CLINIC.primaryColorInput} />
              <span className="text-sm text-muted-foreground">{form.brand_primary_color}</span>
            </div>
          </div>
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Color secundario</Label>
            <div className="flex items-center gap-2">
              <Input type="color" value={form.brand_secondary_color} onChange={set("brand_secondary_color")} className="w-14 p-1" data-testid={CLINIC.secondaryColorInput} />
              <span className="text-sm text-muted-foreground">{form.brand_secondary_color}</span>
            </div>
          </div>
        </div>

        <div className="pt-2">
          <Button onClick={handleSave} disabled={saving} className="bg-brand hover:bg-brand-hover text-white" data-testid={CLINIC.saveButton}>
            {saving ? "Guardando..." : "Guardar cambios"}
          </Button>
        </div>
      </div>

      <PublicBookingSettingsCard clinic={form} onUpdated={setForm} />
      <ClinicBusinessHoursCard clinic={form} onUpdated={setForm} />
      <AcceptedPaymentMethodsCard clinic={form} onUpdated={setForm} />
      <FinancingSettingsCard clinic={form} onUpdated={setForm} />

      <div className="pt-4">
        <h2 className="font-serif text-2xl text-foreground">Página de inicio</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Contenido público de la landing (strani.com), lo primero que ve una visita. {/* Fix AZ (2026-10-30): la doctora y el equipo de la página salen de Profesionales */}
          La sección de la Dra. Strani y &quot;Nuestro equipo&quot; se arman desde <Link to="/profesionales" className="text-brand underline" data-testid={LANDING_ADMIN.profileLink}>Profesionales</Link>
          (bloque &quot;Página web&quot; de cada profesional), no desde aquí.
        </p>
      </div>
      <LandingContentSettingsCard />
      <TestimonialsSettingsCard />
      <GallerySettingsCard />
      {/* Fix BD (2026-10-30): novedades de redes */}
      <NewsPostsSettingsCard />

      <PublicPaymentSettingsCard clinic={form} onUpdated={setForm} />
      <PublicWhatsAppHelpCard clinic={form} onUpdated={setForm} />
      <RequiredPatientFieldsCard clinic={form} onUpdated={setForm} />
      <FollowUpMessageSettingsCard clinic={form} onUpdated={setForm} />
      <ZoneCatalogSettingsCard />
      <SquareSettingsCard />
      <WhatsAppMetaCard />
    </div>
  );
}
